const DATABASE_NAME = 'hivecloud-device-identity'
const STORE_NAME = 'keys'
const KEY_ID = 'ed25519-v1'

interface StoredDeviceKey {
  id: typeof KEY_ID
  privateKey: CryptoKey
  publicKey: CryptoKey
  createdAt: string
}

let deviceKeyPromise: Promise<StoredDeviceKey> | undefined
let deviceKeyResetPromise: Promise<void> | undefined

const openDatabase = (): Promise<IDBDatabase> => new Promise((resolve, reject) => {
  const request = indexedDB.open(DATABASE_NAME, 1)
  request.onupgradeneeded = () => request.result.createObjectStore(STORE_NAME, { keyPath: 'id' })
  request.onsuccess = () => resolve(request.result)
  request.onerror = () => reject(request.error ?? new Error('Unable to open the device key store'))
})

const readKey = async (database: IDBDatabase): Promise<StoredDeviceKey | undefined> => new Promise((resolve, reject) => {
  const request = database.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(KEY_ID)
  request.onsuccess = () => resolve(request.result as StoredDeviceKey | undefined)
  request.onerror = () => reject(request.error ?? new Error('Unable to read the device key'))
})

const writeKey = async (database: IDBDatabase, value: StoredDeviceKey): Promise<void> => new Promise((resolve, reject) => {
  const transaction = database.transaction(STORE_NAME, 'readwrite')
  transaction.objectStore(STORE_NAME).put(value)
  transaction.oncomplete = () => resolve()
  transaction.onerror = () => reject(transaction.error ?? new Error('Unable to persist the device key'))
})

const deleteKey = async (database: IDBDatabase): Promise<void> => new Promise((resolve, reject) => {
  const transaction = database.transaction(STORE_NAME, 'readwrite')
  transaction.objectStore(STORE_NAME).delete(KEY_ID)
  transaction.oncomplete = () => resolve()
  transaction.onerror = () => reject(transaction.error ?? new Error('Unable to reset the device key'))
  transaction.onabort = () => reject(transaction.error ?? new Error('Unable to reset the device key'))
})

export const supportsSecureDeviceKey = async (): Promise<boolean> => {
  if (!globalThis.crypto?.subtle || !globalThis.indexedDB) return false
  try {
    const keys = await ensureDeviceKey()
    return keys.privateKey.extractable === false
      && await matchesDeviceKeyPair(keys.privateKey, keys.publicKey)
  } catch {
    return false
  }
}

export const matchesDeviceKeyPair = async (privateKey: CryptoKey, publicKey: CryptoKey): Promise<boolean> => {
  try {
    if (privateKey.algorithm.name !== 'Ed25519' || publicKey.algorithm.name !== 'Ed25519') return false
    if (!privateKey.usages.includes('sign') || !publicKey.usages.includes('verify')) return false
    const challenge = crypto.getRandomValues(new Uint8Array(32))
    const signature = await crypto.subtle.sign('Ed25519', privateKey, challenge)
    return await crypto.subtle.verify('Ed25519', publicKey, signature, challenge)
  } catch {
    return false
  }
}

const loadOrCreateDeviceKey = async (): Promise<StoredDeviceKey> => {
  const database = await openDatabase()
  try {
    const existing = await readKey(database)
    if (existing && await matchesDeviceKeyPair(existing.privateKey, existing.publicKey)) return existing
    if (existing) {
      throw new Error('The persisted device identity is invalid; remove this browser device before signing in again')
    }
    const keys = await crypto.subtle.generateKey({ name: 'Ed25519' }, false, ['sign', 'verify'])
    if (keys.privateKey.extractable) throw new Error('The browser created an exportable private key')
    const value: StoredDeviceKey = {
      id: KEY_ID,
      privateKey: keys.privateKey,
      publicKey: keys.publicKey,
      createdAt: new Date().toISOString(),
    }
    await writeKey(database, value)
    return value
  } finally {
    database.close()
  }
}

export const ensureDeviceKey = (): Promise<StoredDeviceKey> => {
  deviceKeyPromise ??= (deviceKeyResetPromise
    ? deviceKeyResetPromise.then(loadOrCreateDeviceKey)
    : loadOrCreateDeviceKey()).catch((failure) => {
    deviceKeyPromise = undefined
    throw failure
  })
  return deviceKeyPromise
}

export const resetDeviceKey = (): Promise<void> => {
  if (deviceKeyResetPromise) return deviceKeyResetPromise
  const pendingKey = deviceKeyPromise
  deviceKeyPromise = undefined
  const reset = (async () => {
    await pendingKey?.catch(() => undefined)
    const database = await openDatabase()
    try {
      await deleteKey(database)
    } finally {
      database.close()
    }
  })()
  deviceKeyResetPromise = reset.finally(() => { deviceKeyResetPromise = undefined })
  return deviceKeyResetPromise
}

export const publicDeviceKey = async (): Promise<Uint8Array<ArrayBuffer>> => {
  const key = await ensureDeviceKey()
  return new Uint8Array(await crypto.subtle.exportKey('raw', key.publicKey))
}

export const signDeviceAuthorization = async (
  nonce: string,
  clientId: string,
  label: string,
  sessionProfile: 'TEMPORARY' | 'TRUSTED',
): Promise<Uint8Array<ArrayBuffer>> => {
  const key = await ensureDeviceKey()
  const normalizedLabel = label.trim().normalize('NFC')
  const proof = `hive-device-authorization-v2\n${nonce}\n${clientId}\n${normalizedLabel}\n${sessionProfile}`
  return new Uint8Array(await crypto.subtle.sign('Ed25519', key.privateKey, new TextEncoder().encode(proof)))
}
